import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';

import { EmptyStateComponent } from '../../../components/ui/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../components/ui/error-state/error-state.component';
import { Test } from '../../../core/interfaces/test';
import { AdminTestService } from '../../../core/services/admin/admin-test.service';
import { TestService } from '../../../core/services/test.service';
import { SwalService } from '../../../core/services/utils/swal.service';
import { matchesSearch } from '../../../core/utils/search-text';
import { SubjectChoiceService } from '../../../core/services/utils/subject-choice.service';

/** Une option de filtre : un identifiant et son libellé. */
interface FilterOption {
  id: number;
  label: string;
}

/** Modes de test, dans l'ordre où ils sont proposés à la composition. */
const TEST_MODES = ['exam', 'evaluation', 'practice'] as const;

/**
 * Lignes par page.
 *
 * <p>La liste était rendue d'un bloc : un établissement qui compose plusieurs
 * centaines de tests obtenait une page interminable, lente à peindre et
 * impossible à parcourir. Le filtrage restant local, la pagination l'est
 * aussi — elle découpe ce qui a déjà été filtré.</p>
 */
const PAGE_SIZE = 25;

/**
 * Tous les tests de l'établissement, vus par l'administration.
 *
 * <p>Le professeur ne voit que les siens (`api/professor/tests`, périmètre
 * dérivé du jeton). Cet écran est le seul endroit d'où l'on voit la production
 * de tout le monde, et le seul d'où l'on peut reprendre le test d'un professeur
 * absent.</p>
 *
 * <p>Le filtrage est local : la liste tient dans une requête, et croiser
 * professeur × parcours × matière côté serveur imposerait quatre paramètres et
 * autant d'allers-retours pour un gain nul à cette échelle. Les options des
 * filtres sont dérivées des tests chargés — pas du référentiel complet — afin
 * qu'aucune option ne mène à une liste vide.</p>
 */
@Component({
  selector: 'app-admin-tests',
  standalone: true,
  imports: [DatePipe, TranslateModule, EmptyStateComponent, ErrorStateComponent],
  templateUrl: './admin-tests.component.html',
  styleUrl: './admin-tests.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminTestsComponent implements OnInit {
  private readonly adminTests = inject(AdminTestService);
  private readonly testService = inject(TestService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly subjectChoice = inject(SubjectChoiceService);

  readonly modes = TEST_MODES;

  readonly tests = signal<Test[]>([]);
  readonly loading = signal(false);
  readonly errored = signal(false);
  /** Détail technique affiché sous le message d'erreur (statut HTTP, message serveur). */
  readonly errorDetail = signal('');

  /** Test dont l'action est en cours ; sert à ne verrouiller que sa ligne. */
  readonly busyId = signal<number | null>(null);

  // ── Facettes ──────────────────────────────────────────────
  readonly professorId = signal<number | null>(null);
  readonly learningPathId = signal<number | null>(null);
  readonly subjectId = signal<number | null>(null);
  readonly mode = signal<string | null>(null);
  readonly search = signal('');

  /**
   * Professeurs présents dans la liste.
   *
   * <p>Un test dont le serveur n'a pas hydraté le professeur est rangé sous son
   * identifiant plutôt que sous un nom vide : mieux vaut une ligne identifiable
   * qu'une entrée anonyme dans laquelle plusieurs professeurs se confondraient.</p>
   */
  readonly professorOptions = computed(() =>
    this.optionsFrom(this.tests(), (t) => ({
      id: Number(t.professorId),
      label: t.professorName?.trim() || `#${t.professorId}`,
    })),
  );

  readonly learningPathOptions = computed(() =>
    this.optionsFrom(this.tests(), (t) =>
      t.learningPathId == null
        ? null
        : { id: t.learningPathId, label: t.learningPathTitle ?? `#${t.learningPathId}` },
    ),
  );

  /**
   * Matières, restreintes au parcours choisi.
   *
   * <p>Tant qu'aucun parcours n'est choisi — l'état par défaut —, la liste les
   * couvre tous et le même nom de matière y revient une fois par parcours. Le
   * parcours est alors ajouté aux seuls noms en collision ; une fois un parcours
   * choisi, plus aucune collision n'est possible et les libellés redeviennent
   * nus d'eux-mêmes.</p>
   */
  readonly subjectOptions = computed(() => {
    const pathId = this.learningPathId();
    const scoped = pathId == null
      ? this.tests()
      : this.tests().filter((t) => t.learningPathId === pathId);

    // Dédoublonnage à part plutôt que par `optionsFrom` : le libellé n'est pas
    // connu à ce stade, il dépend de l'ensemble une fois celui-ci constitué.
    const byId = new Map<number, { id: number; name: string; pathLabel: string | null }>();
    for (const test of scoped) {
      if (!Number.isFinite(test.subjectId) || byId.has(test.subjectId)) continue;
      byId.set(test.subjectId, {
        id: test.subjectId,
        name: test.subjectName ?? `#${test.subjectId}`,
        pathLabel: test.learningPathTitle ?? null,
      });
    }

    const raw = [...byId.values()];
    const labels = this.subjectChoice.disambiguate(raw);

    return raw
      .map((option, index) => ({ id: option.id, label: labels[index] }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  readonly page = signal(1);

  readonly filtered = computed(() => {
    const professorId = this.professorId();
    const learningPathId = this.learningPathId();
    const subjectId = this.subjectId();
    const mode = this.mode();
    const needle = this.search();

    return this.tests().filter((t) => {
      if (professorId != null && Number(t.professorId) !== professorId) return false;
      if (learningPathId != null && t.learningPathId !== learningPathId) return false;
      if (subjectId != null && t.subjectId !== subjectId) return false;
      if (mode && t.testMode !== mode) return false;
      if (!matchesSearch(needle, t.title)) return false;
      return true;
    });
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filtered().length / PAGE_SIZE)),
  );

  /** Page courante, bornée : un filtre peut avoir réduit le nombre de pages. */
  readonly currentPage = computed(() => Math.min(this.page(), this.totalPages()));

  readonly paged = computed(() => {
    const start = (this.currentPage() - 1) * PAGE_SIZE;
    return this.filtered().slice(start, start + PAGE_SIZE);
  });

  readonly canPrevious = computed(() => !this.loading() && this.currentPage() > 1);
  readonly canNext = computed(
    () => !this.loading() && this.currentPage() < this.totalPages(),
  );

  readonly activeCount = computed(() => {
    let n = 0;
    if (this.professorId() != null) n++;
    if (this.learningPathId() != null) n++;
    if (this.subjectId() != null) n++;
    if (this.mode()) n++;
    if (this.search().trim()) n++;
    return n;
  });

  readonly hasActiveFilters = computed(() => this.activeCount() > 0);

  /** Succès mais aucune ligne : état vide, jamais de contenu de substitution. */
  readonly isEmpty = computed(
    () => !this.loading() && !this.errored() && this.filtered().length === 0,
  );

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.errored.set(false);
    this.errorDetail.set('');

    this.adminTests
      .allTests()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          if (response?.status !== 'SUCCESS') {
            // Une enveloppe en échec n'est pas une liste vide : la distinguer
            // évite d'annoncer « aucun test » alors que rien n'a été lu.
            this.fail(response?.message ?? '');
            return;
          }
          this.tests.set(response.data ?? []);
        },
        error: (error) =>
          this.fail(error?.status ? `HTTP ${error.status}` : (error?.message ?? '')),
      });
  }

  // ── Facettes ──────────────────────────────────────────────
  onProfessor(raw: string): void {
    this.professorId.set(raw ? Number(raw) : null);
    this.page.set(1);
  }

  onLearningPath(raw: string): void {
    this.learningPathId.set(raw ? Number(raw) : null);
    // La matière retenue appartient probablement à l'ancien parcours : la
    // garder croiserait deux portées et ne renverrait rien.
    this.subjectId.set(null);
    this.page.set(1);
  }

  onSubject(raw: string): void {
    this.subjectId.set(raw ? Number(raw) : null);
    this.page.set(1);
  }

  onMode(raw: string): void {
    this.mode.set(raw || null);
    this.page.set(1);
  }

  onSearch(raw: string): void {
    this.search.set(raw);
    // Changer une facette invalide la position : rester page 7 sur un
    // resultat qui n'en compte plus que 2 afficherait du vide.
    this.page.set(1);
  }

  resetFilters(): void {
    this.professorId.set(null);
    this.learningPathId.set(null);
    this.subjectId.set(null);
    this.mode.set(null);
    this.search.set('');
    this.page.set(1);
  }

  previousPage(): void {
    if (this.canPrevious()) this.page.set(this.currentPage() - 1);
  }

  nextPage(): void {
    if (this.canNext()) this.page.set(this.currentPage() + 1);
  }

  // ── Actions ───────────────────────────────────────────────
  openDetail(test: Test): void {
    this.router.navigate(['/admin/test', test.id]);
  }

  editTest(test: Test): void {
    this.router.navigate(['/admin/test/update', test.id]);
  }

  /**
   * Publie ou dépublie le test.
   *
   * <p>Le serveur bascule lui-même (`draft` ↔ `published`) : on lui redemande
   * l'état obtenu plutôt que de le deviner ici, où l'on finirait par diverger
   * d'un test passé entre-temps en relecture.</p>
   */
  togglePublish(test: Test): void {
    const published = test.status === 'published';
    this.swal.confirmPublishToggle(published, () => {
      this.busyId.set(test.id);
      this.testService
        .updateStatus(test.id, published ? 'draft' : 'published')
        .pipe(finalize(() => this.busyId.set(null)))
        .subscribe({
          next: (response) => {
            if (response?.status !== 'SUCCESS') return;
            this.swal.success(this.translate.instant('SWAL.UPDATED'));
            this.load();
          },
          // L'intercepteur global signale déjà l'échec réseau ou serveur.
          error: () => undefined,
        });
    });
  }

  deleteTest(test: Test): void {
    this.swal.confirm(
      this.translate.instant('ADMIN_TESTS.DELETE_CONFIRM', { title: test.title }),
      () => {
        this.busyId.set(test.id);
        this.testService
          .delete(test.id)
          .pipe(finalize(() => this.busyId.set(null)))
          .subscribe({
            next: () => {
              this.swal.success(this.translate.instant('SWAL.DELETED'));
              this.load();
            },
            error: () => undefined,
          });
      },
    );
  }

  // ── Libellés ──────────────────────────────────────────────
  modeKey(mode: string): string {
    return `TEST_MODE.${(mode ?? '').toUpperCase()}`;
  }

  /**
   * Les statuts du serveur sont en `snake_case` (`pending_review`) et les clés
   * i18n en `SCREAMING_SNAKE_CASE` : la conversion est faite ici plutôt que
   * dans le gabarit, pour qu'un statut inattendu produise une clé visible
   * plutôt qu'un libellé silencieusement faux.
   */
  statusKey(status: string): string {
    return `ADMIN_TESTS.STATUS.${(status ?? '').toUpperCase()}`;
  }

  professorLabel(test: Test): string {
    return test.professorName?.trim() || '—';
  }

  learningPathLabel(test: Test): string {
    return test.learningPathTitle?.trim() || '—';
  }

  // ── Interne ───────────────────────────────────────────────
  private fail(detail: string): void {
    this.tests.set([]);
    this.errorDetail.set(detail);
    this.errored.set(true);
  }

  /** Options distinctes tirées des tests, triées par libellé. */
  private optionsFrom(
    tests: Test[],
    pick: (test: Test) => FilterOption | null,
  ): FilterOption[] {
    const byId = new Map<number, string>();
    for (const test of tests) {
      const option = pick(test);
      if (!option || !Number.isFinite(option.id)) continue;
      byId.set(option.id, option.label);
    }
    return [...byId.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }
}
